class CollectionDropdownComponent extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      gridContainer: '[data-product-grid-container]',
      clearButton: '[data-collection-clear-filters]',
      filterCount: '[data-collection-filter-count]',
      productCount: '[data-collection-product-count]',
      clearSearch: '[data-collection-clear-search]',
    };
    this.filterLabelCache = new WeakMap();
  }

  connectedCallback() {
    this.dropdown = this;
    this.selected = this.dropdown.querySelector('[data-dropdown-selected]');
    this.selectedText = this.selected.querySelector('span');
    this.list = this.dropdown.querySelector('[data-dropdown-list]');
    this.options = this.list.querySelectorAll('li');
    this.clearSearch = document.querySelector('[data-collection-clear-search]');
    this.type = this.dropdown.dataset.type;
    this.url = new URL(window.location.href);

    const originalLabel = this.selectedText?.textContent?.trim() || '';
    if (!this.filterLabelCache.has(this.dropdown)) {
      this.filterLabelCache.set(this.dropdown, originalLabel);
    }

    this.setDropdownAria(false);
    this.setInitialSelection();
    this.selected.addEventListener('click', this.toggleList.bind(this));
    this.selected.addEventListener('keydown', this.onButtonKeydown.bind(this));
    this.options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick.bind(this, option));
      option.addEventListener('keydown', this.onOptionKeydown.bind(this, option));
    });
    document.addEventListener('click', this.onOutsideClick.bind(this));
    document.addEventListener('click', this.onOutsideClick.bind(this));
    this.clearSearch.addEventListener('click', this.handleSearchClear.bind(this));

    this.updateFilterCount();
    this.bindClearButton();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState.bind(this));
  }

  handlePopState() {
    this.url = new URL(window.location.href);
    this.setInitialSelection();
    this.updateCollectionSections(this.url.pathname + this.url.search);
    if (this.type === 'filter') this.updateFilterCount();
  }

  handleSearchClear() {
    window.location.href = '/collections/all';
  }

  setDropdownAria(expanded) {
    this.list.style.display = expanded ? 'block' : 'none';
    this.selected.setAttribute('aria-expanded', expanded);
    this.list.setAttribute('aria-hidden', !expanded);
  }

  toggleList() {
    const isOpen = this.list.style.display === 'block';
    this.setDropdownAria(!isOpen);
    if (!isOpen) {
      this.focusFirstOption();
      // Close other custom filters when opening this dropdown
      this.closeCustomFilters();
    }
  }

  onButtonKeydown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleList();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.setDropdownAria(true);
      this.focusFirstOption();
    }
  }

  onOptionKeydown(option, e) {
    const options = Array.from(this.options);
    const currentIndex = options.indexOf(option);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = options[(currentIndex + 1) % options.length];
      next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = options[(currentIndex - 1 + options.length) % options.length];
      prev.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      option.click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.setDropdownAria(false);
      this.selected.focus();
    }
  }

  onOptionClick(option) {
    const value = option.dataset.value;
    const newUrl = new URL(window.location.href);

    if (this.type === 'sort') {
      newUrl.searchParams.set('sort_by', value);
    } else if (this.type === 'filter') {
      const filterName = option.dataset.filterName;
      if (filterName) {
        newUrl.searchParams.set(filterName, value);
      }
    }

    history.pushState({}, '', newUrl.toString());
    this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');
    this.selectedText.textContent = option.textContent.trim();
    this.setDropdownAria(false);
    this.updateCollectionSections(newUrl.pathname + newUrl.search);
    if (this.type === 'filter') this.updateFilterCount();
  }

  onOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.setDropdownAria(false);
    }
  }

  focusFirstOption() {
    this.options.forEach((opt) => opt.setAttribute('tabindex', '-1'));
    if (this.options.length > 0) {
      this.options[0].setAttribute('tabindex', '0');
      this.options[0].focus();
    }
  }

  closeCustomFilters() {
    // Close all custom color and varietal filter details elements
    const customFilters = document.querySelectorAll('.custom-color-filter-wrapper');
    customFilters.forEach(filter => {
      if (filter.hasAttribute('open')) {
        filter.removeAttribute('open');
      }
    });
  }

  setInitialSelection() {
    let currentValue = null;

    if (this.type === 'sort') {
      currentValue = this.url.searchParams.get('sort_by');
    } else if (this.type === 'filter') {
      this.options.forEach((option) => {
        const filterName = option.dataset.filterName;
        const filterValue = option.dataset.value;
        if (this.url.searchParams.get(filterName) === filterValue) {
          currentValue = filterValue;
        }
      });
    }

    if (currentValue) {
      this.options.forEach((option) => {
        const isMatch = option.dataset.value === currentValue;
        option.setAttribute('aria-selected', isMatch);
        if (isMatch && this.selectedText) this.selectedText.textContent = option.textContent.trim();
      });
    } else {
      this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      if (this.selectedText) {
        const originalLabel = this.filterLabelCache.get(this.dropdown);
        if (originalLabel) this.selectedText.textContent = originalLabel;
      }
    }
  }

  async updateCollectionSections(url) {
    const sectionName = window.templateName === 'collection' ? 'main-collection-product-grid' : 'main-search';
    const sectionUrl = url.includes('?')
      ? `${url}&sections=${sectionName}`
      : `${url}?view=ajax&sections=${sectionName}`;

    try {
      const response = await fetch(sectionUrl);
      const json = await response.json();
      const htmlString = json[sectionName];
      const html = new DOMParser().parseFromString(htmlString, 'text/html');

      const newGrid = html.querySelector(this.selectors.gridContainer);
      if (newGrid) {
        document.querySelector(this.selectors.gridContainer).innerHTML = newGrid.innerHTML;
      }

      const newCount = html.querySelector(this.selectors.productCount);
      if (newCount) {
        document.querySelectorAll(this.selectors.productCount).forEach((el) => {
          el.outerHTML = newCount.outerHTML;
        });
      }

      const newClear = html.querySelector(this.selectors.clearButton);
      const currentClear = document.querySelector(this.selectors.clearButton);
      if (newClear && currentClear) {
        currentClear.outerHTML = newClear.outerHTML;
        this.bindClearButton();
      }
    } catch (e) {
      console.error('Collection fetch and sync failed.', e);
    }
  }

  updateFilterCount() {
    const countEls = document.querySelectorAll(this.selectors.filterCount);
    const url = new URL(window.location.href);
    const count = [...url.searchParams.keys()].filter((key) => key.startsWith('filter.')).length;

    countEls.forEach((el) => (el.textContent = count));

    const clearButton = document.querySelector(this.selectors.clearButton);
    if (clearButton) {
      clearButton.style.display = count > 0 ? '' : 'none';
    }
  }

  bindClearButton() {
    const button = document.querySelector(this.selectors.clearButton);
    if (!button) return;

    const count = button.querySelector(this.selectors.filterCount);
    const url = new URL(window.location.href);
    const activeFilters = [...url.searchParams.keys()].filter((key) => key.startsWith('filter.'));
    const countValue = activeFilters.length;

    if (countValue > 0) {
      button.style.display = '';
      if (count) count.textContent = countValue;
    } else {
      button.style.display = 'none';
    }

    button.addEventListener('click', () => {
      const cleanUrl = new URL(window.location.href);
      activeFilters.forEach((key) => cleanUrl.searchParams.delete(key));
      history.pushState({}, '', cleanUrl.toString());
      this.updateCollectionSections(cleanUrl.pathname + cleanUrl.search);
      button.style.display = 'none';

      document.querySelectorAll('collection-dropdown').forEach((dropdown) => {
        const selected = dropdown.querySelector('[data-dropdown-selected] span');
        const options = dropdown.querySelectorAll('[data-dropdown-list] li');
        const originalLabel = this.filterLabelCache.get(dropdown);
        if (selected && originalLabel) selected.textContent = originalLabel;
        options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      });

      this.updateFilterCount();
    });
  }
}

customElements.define('collection-dropdown', CollectionDropdownComponent);
